
import { createAction, createSlice } from '@reduxjs/toolkit';
import type { Dispatch } from 'redux';

import type { Contractor, ServiceType } from './../lib/types';
import { contractorApi } from './../lib/api';
import type { ListState, AppState } from './redux';
import { defaultPageSize } from './constants';
import type { FetchListParams } from './../lib/api';
import type { ContractorsFilter } from '../lib/types/filters';

export type { ContractorsFilter } from '../lib/types/filters';

export const DELETE_CONTRACTOR = 'vehicles/contractors/delete';
export const FETCH_CONTRACTORS = 'vehicles/contractors/fetch';
export const SET_CONTRACTORS_FILTERS = 'vehicles/contractors/filters/set';
export const CLEAN_CONTRACTORS_FILTERS = 'vehicles/contractors/filters/clean';

export type ContractorsState = ListState<Contractor> & {
  filters: ContractorsFilter
};

type ContractorsFetchPayload = {
  data: Contractor[];
  totalCount: number;
  page: number;
  pageSize: number;
};

type ContractorsThunkDispatch = Dispatch & {
  <Result>(
    thunk: (dispatch: Dispatch, getState: () => AppState) => Result
  ): Result;
};

const initialState: ContractorsState = {
  data: [],
  page: 1,
  pageSize: defaultPageSize,
  totalCount: 0,
  filters: {}
};

const deleteContractorAction = createAction<number>(DELETE_CONTRACTOR);
const fetchContractorsAction = createAction<ContractorsFetchPayload>(
  FETCH_CONTRACTORS
);
const setContractorsFiltersAction = createAction<ContractorsFilter>(
  SET_CONTRACTORS_FILTERS
);
const cleanContractorsFiltersAction = createAction(CLEAN_CONTRACTORS_FILTERS);

const contractorsSlice = createSlice({
  name: 'vehicles/contractors',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(deleteContractorAction, (state, { payload }) => ({
        ...state,
        data: state.data.filter(contractor => contractor.id !== payload)
      }))
      .addCase(fetchContractorsAction, (state, { payload }) => ({
        ...state,
        ...payload
      }))
      .addCase(setContractorsFiltersAction, (state, { payload }) => ({
        ...state,
        filters: payload
      }))
      .addCase(cleanContractorsFiltersAction, state => ({
        ...state,
        filters: {}
      }));
  }
});

export const contractorsActions = {
  delete: deleteContractorAction,
  fetch: fetchContractorsAction,
  setFilters: setContractorsFiltersAction,
  cleanFilters: cleanContractorsFiltersAction
};

export const fetchContractors = (
  page: number = 1,
  params: FetchListParams<ContractorsFilter> = {}
) => async (
  dispatch: Dispatch,
  getState: () => AppState
): Promise<void> => {
  const { filters, pageSize } = getState().contractors;
  const { data, totalCount } = await contractorApi.fetchContractors({
    ...filters,
    page,
    pageSize,
    ...params
  });
  dispatch(
    contractorsActions.fetch({
      data,
      totalCount,
      page,
      pageSize
    })
  );
};

export const applyFilters = (filters: ContractorsFilter) => async (
  dispatch: ContractorsThunkDispatch
) => {
  dispatch(contractorsActions.setFilters(filters));
  dispatch(fetchContractors());
};

export const cleanFilters = () => async (dispatch: ContractorsThunkDispatch) => {
  dispatch(contractorsActions.cleanFilters());
  dispatch(fetchContractors());
};

export default contractorsSlice.reducer;
